import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { UsersThree, UserPlus, Plus, Lightbulb, X } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistId,
} from "../../../store/slices/authSlice";
import {
  useGetClinicTherapistsQuery,
  useGetTeamPatientCountsQuery,
} from "../../../store/services/therapistApi";
import { getUsageSummary } from "../../../services/planService";
import { PageHeader, HelpButton } from "../../../components/shared";
import CustomClinikitActionButton from "../../../components/ui/CustomClinikitActionButton";
import { deriveClinicRole } from "../../../config/permissionsRegistry";
import TeamMemberPopup from "../../../components/team/TeamMemberPopup";
import AddTeamMemberModal from "../../../components/team/AddTeamMemberModal";
import MultiClinicUpgradedModal from "../../../components/team/MultiClinicUpgradedModal";
import {
  memberName,
  memberInitials,
  roleBadge,
  lastLoginText,
  overriddenKeys,
} from "../../../components/team/teamUtils";
import ContentLoader from "../../../components/shared/ContentLoader";
import "../../../components/team/team.css";

const ROLE_ORDER = { owner: 0, manager: 1, therapist: 2, secretary: 3 };

const introSeenKey = (therapistId) => `ck_team_intro_seen_${therapistId}`;

function readIntroSeen(therapistId) {
  if (!therapistId) return true;
  try {
    return window.localStorage.getItem(introSeenKey(therapistId)) === "1";
  } catch {
    return true;
  }
}

function writeIntroSeen(therapistId) {
  if (!therapistId) return;
  try {
    window.localStorage.setItem(introSeenKey(therapistId), "1");
  } catch {
    // localStorage unavailable (private mode / blocked) — dismiss is in-memory only.
  }
}

/**
 * צוות הקליניקה — admin Team page (multi-clinic v2, Phase 1).
 * Route: /dashboard/admin/team (admin tier). Solo owners reach it from
 * Clinic Settings → "ניהול צוות" (not in the sidebar).
 */
function TeamPage() {
  const clinicId = useSelector(selectClinicId);
  const clinic = useSelector(selectClinicProfile);
  const myId = useSelector(selectTherapistId);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: therapists = [], isLoading } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId },
  );

  const sorted = useMemo(
    () =>
      [...therapists].sort((a, b) => {
        const ra = ROLE_ORDER[deriveClinicRole(a)] ?? 9;
        const rb = ROLE_ORDER[deriveClinicRole(b)] ?? 9;
        if ((a.isActive === false) !== (b.isActive === false)) return a.isActive === false ? 1 : -1;
        if (ra !== rb) return ra - rb;
        return memberName(a).localeCompare(memberName(b), "he");
      }),
    [therapists],
  );

  const activeCount = therapists.filter((t) => t.isActive !== false).length;
  const inactiveCount = therapists.length - activeCount;

  const therapistIds = useMemo(
    () => therapists.filter((t) => deriveClinicRole(t) !== "secretary").map((t) => t.uid || t.id),
    [therapists],
  );
  const { data: patientCounts = {} } = useGetTeamPatientCountsQuery(
    { clinicId, therapistIds },
    { skip: !clinicId || therapistIds.length === 0 },
  );

  // Seat usage (secretaries count)
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!clinicId) return undefined;
    getUsageSummary(clinicId)
      .then((u) => {
        if (!cancelled && u?.usage?.therapists) setSeats(u.usage.therapists);
      })
      .catch((err) => {
        console.error("[CK-5546] Error loading seat usage:", {
          errorCode: "CK-5546",
          errorMessage: err?.message,
          clinicId,
          timestamp: new Date().toISOString(),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, therapists.length]);

  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(searchParams.get("add") === "1");
  const [showUpgraded, setShowUpgraded] = useState(false);
  const [pendingUpgraded, setPendingUpgraded] = useState(false);

  // One-time coach-mark (per therapist, multi clinics only)
  const [introDismissed, setIntroDismissed] = useState(() => readIntroSeen(myId));
  useEffect(() => {
    setIntroDismissed(readIntroSeen(myId));
  }, [myId]);
  const showIntro = !introDismissed && !!clinic && clinic.isSoloClinic !== true;
  const dismissIntro = () => {
    writeIntroSeen(myId);
    setIntroDismissed(true);
  };
  const selected = useMemo(
    () => therapists.find((t) => (t.uid || t.id) === selectedId) || null,
    [therapists, selectedId],
  );

  const closeAdd = () => {
    setShowAdd(false);
    if (searchParams.get("add")) {
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
    // Show the "now multi-clinic" explainer only after the add modal is gone.
    if (pendingUpgraded) {
      setPendingUpgraded(false);
      setShowUpgraded(true);
    }
  };

  const handleCreated = (info) => {
    if (info?.upgradedToMulti) setPendingUpgraded(true);
  };

  const seatText =
    seats && Number.isFinite(seats.limit) && seats.limit > 0
      ? `${seats.current} מתוך ${seats.limit} מקומות בתוכנית`
      : null;

  return (
    <div className="tm-page">
      <PageHeader
        icon={UsersThree}
        title="צוות הקליניקה"
        subtitle="תפקידים, הרשאות ומטופלים לכל איש/אשת צוות"
        actions={
          <div className="tm-header-actions">
            <HelpButton featureKey="team" />
            <CustomClinikitActionButton icon={<Plus size={16} weight="bold" />} onClick={() => setShowAdd(true)}>
              הוספת איש/אשת צוות
            </CustomClinikitActionButton>
          </div>
        }
      />

      <div className="tm-meta">
        <span className="tm-chip primary">{activeCount} פעילים</span>
        <span className="tm-chip">{inactiveCount} לא פעילים</span>
        {seatText && <span className="tm-chip muted">{seatText}</span>}
        {clinic?.isSoloClinic && <span className="tm-chip warning">קליניקת מטפל/ת יחיד/ה — הוספת איש צוות תפתח את אזור הניהול</span>}
      </div>

      {isLoading ? (
        <ContentLoader message="טוען צוות…" />
      ) : sorted.length === 0 ? (
        <div className="tm-empty">
          <p>עדיין אין אנשי צוות נוספים.</p>
          <CustomClinikitActionButton icon={<UserPlus size={16} weight="bold" />} onClick={() => setShowAdd(true)}>
            הוספת איש/אשת צוות ראשון/ה
          </CustomClinikitActionButton>
        </div>
      ) : (
        <div className="tm-grid">
          {showIntro && (
            <div className="tm-intro-card" role="note">
              <span className="tm-intro-icon">
                <Lightbulb size={18} weight="fill" />
              </span>
              <div className="tm-intro-text">
                <strong>כאן מגדירים תפקידים והרשאות</strong>
                <span>
                  לחצו על כרטיס של איש/אשת צוות כדי לבחור תפקיד ולפתוח או לסגור הרשאות. הרשאה אישית גוברת על ברירת המחדל של הקליניקה.
                </span>
              </div>
              <button type="button" className="tm-intro-dismiss" onClick={dismissIntro} aria-label="סגירה">
                <X size={16} weight="bold" />
              </button>
            </div>
          )}
          {sorted.map((t) => {
            const id = t.uid || t.id;
            const role = deriveClinicRole(t);
            const inactive = t.isActive === false;
            const overrides = role === "therapist" || role === "secretary" ? overriddenKeys(t, clinic) : [];
            return (
              <button
                key={id}
                type="button"
                className={`tm-card ${inactive ? "inactive" : ""}`}
                onClick={() => setSelectedId(id)}
              >
                {id === myId && <span className="tm-card-me">את/ה</span>}
                <div className="tm-card-top">
                  <span className="tm-avatar">
                    {t.profileImage ? <img src={t.profileImage} alt="" /> : memberInitials(t)}
                    <span className={`tm-status-dot ${inactive ? "off" : ""}`} />
                  </span>
                  <span className="tm-card-name">
                    <strong>{memberName(t)}</strong>
                    <span className="tm-card-sub">{lastLoginText(t)}</span>
                  </span>
                </div>
                <div className="tm-card-bottom">
                  <div className="tm-card-chips">
                    <span className={`tm-role-badge ${role}`}>{roleBadge(t)}</span>
                    {t.inviteStatus === "pending" && !inactive && (
                      <span className="tm-chip warning">ממתין/ה לכניסה ראשונה</span>
                    )}
                    {overrides.length > 0 && <span className="tm-chip warning">הרשאות מותאמות</span>}
                  </div>
                  <span>
                    {role === "secretary"
                      ? "ללא יומן ומטופלים אישיים"
                      : `${patientCounts[id] ?? "–"} מטופלים`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && <TeamMemberPopup therapist={selected} onClose={() => setSelectedId(null)} />}
      {showAdd && <AddTeamMemberModal onClose={closeAdd} onCreated={handleCreated} />}
      {showUpgraded && <MultiClinicUpgradedModal onClose={() => setShowUpgraded(false)} />}
    </div>
  );
}

export default TeamPage;
